import { Notice, Button, Select, Stack, Tooltip, Flex, TimeField } from '@eleveo/components-react';
import { BinIcon, CrossIcon } from '@eleveo/icons-react';
import { TFunction } from 'i18next';
import React from 'react';
import { Field, FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { ShiftBreakType } from '../../graphql/components';
import { toSecondsFromTime, toTimeFromSeconds } from '../../utils/time';
import {
  getFieldsInputTag,
  REMOVE_BREAK_BUTTON,
  RESET_BREAK_SETTINGS_BUTTON,
  SHIFT_BREAK_CONTAINER,
} from './constants';
import { initializeBreakValue } from './utils';

import type { ShiftTemplatesFormValues } from './types';
import type { FormApi } from 'final-form';

const createBreakTypeOptions = (t: TFunction) =>
  Object.values(ShiftBreakType).map((type) => ({
    id: type,
    name: t(`shiftTemplate.breakType.${type}`),
  }));

type Props = {
  currentDayTemplateIndex: number;
  fieldPrefix: string;
  form: FormApi<ShiftTemplatesFormValues, Partial<ShiftTemplatesFormValues>>;
  index: number;
  isLast: boolean;
  parentPrefix: string;
};

/**
 * validation of inner fields relies on common validation of dayTemplates values,
 * so there is no need to provide one more time the same validation separately on these fields
 */
export const BreakTemplate: React.FC<Props> = ({
  currentDayTemplateIndex,
  fieldPrefix,
  form,
  index,
  isLast,
  parentPrefix,
}) => {
  const { t } = useTranslation();

  const {
    mutators: { remove, update },
  } = form;

  return (
    <Stack direction="vertical" spacing={15} testId={`${SHIFT_BREAK_CONTAINER}-${index}`} className="w-full">
      <Flex gap={15}>
        <Field name={`${fieldPrefix}.type`}>
          {({ input, meta }) => (
            <Select
              className="w-full"
              isClearable
              label={t('shiftTemplate.breakType.breakType')}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              onSelectionChange={(values) => input.onChange(values.values().next().value)}
              options={createBreakTypeOptions(t)}
              selectedKeys={input.value ? [input.value] : []}
              selectionMode="single"
              testId={getFieldsInputTag('break:type')}
              validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
            />
          )}
        </Field>
        <Field name={`${fieldPrefix}.duration`}>
          {({ input, meta }) => (
            <TimeField
              testId={getFieldsInputTag('break:duration')}
              label={t('time.duration')}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              onChange={(value) => {
                input.onChange(value ? toSecondsFromTime(value) : null);
              }}
              value={Number.isInteger(input.value) ? toTimeFromSeconds(input.value) : null}
              steps={{ minute: 15 }}
              hourCycle={24}
              validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
              className="w-full"
            />
          )}
        </Field>
      </Flex>
      <Stack direction="horizontal">
        <Field name={`${fieldPrefix}.entitlementAfter`}>
          {({ input, meta }) => (
            <TimeField
              testId={getFieldsInputTag('break:entitlement-after')}
              label={t('shiftTemplate.breakEntitlementAfter')}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              onChange={(value) => {
                input.onChange(value ? toSecondsFromTime(value) : null);
              }}
              value={Number.isInteger(input.value) ? toTimeFromSeconds(input.value) : null}
              steps={{ minute: 15 }}
              hourCycle={24}
              validationState={meta.touched && meta.invalid ? 'invalid' : undefined}
              className="w-full"
            />
          )}
        </Field>

        <Tooltip content={t('form.resetChanges')}>
          <Button
            aria-label={t('form.resetChanges')}
            color="warning"
            onClick={() => update(parentPrefix, index, initializeBreakValue())}
            testId={RESET_BREAK_SETTINGS_BUTTON}
            variant="outline"
          >
            <CrossIcon />
          </Button>
        </Tooltip>
        <Tooltip content={t('layout.action.delete')}>
          <Button
            aria-label={t('layout.action.delete')}
            color="error"
            isDisabled={isLast && index === 0}
            onClick={() => remove(parentPrefix, index)}
            testId={REMOVE_BREAK_BUTTON}
            variant="outline"
          >
            <BinIcon />
          </Button>
        </Tooltip>
      </Stack>

      <FormSpy<ShiftTemplatesFormValues> subscription={{ values: true }}>
        {({ values }) => {
          const shiftDuration = values.dayTemplates[currentDayTemplateIndex]?.shiftDuration;
          const { entitlementAfter, duration, type } =
            values.dayTemplates[currentDayTemplateIndex]?.breaks?.[index] ?? {};

          const notices: JSX.Element[] = [];

          if (shiftDuration !== undefined && entitlementAfter !== undefined && entitlementAfter > shiftDuration) {
            notices.push(
              <Notice key="duration" color="error">
                {t('shiftTemplate.breakEntitlementAfterHigherThanShiftDuration')}
              </Notice>
            );
          }

          let filledFields = 0;

          if (Number.isInteger(entitlementAfter)) {
            filledFields += 1;
          }
          if (Number.isInteger(duration)) {
            filledFields += 1;
          }
          if (type) {
            filledFields += 1;
          }

          // Do not show warning message when all of fields or none of them are filled in
          if (filledFields > 0 && filledFields < 3) {
            notices.push(
              <Notice key="empty" color="warning">
                {t('shiftTemplate.breakMandatoryFieldsAreEmpty', { count: 3 - filledFields })}
              </Notice>
            );
          }

          return notices;
        }}
      </FormSpy>
    </Stack>
  );
};
